import { Card, Flex, Spinner, Text as RadixText } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

export function CameraPreviewCard({ image }: { image: string | null }) {
  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">(
    "idle"
  );

  useEffect(() => {
    if (!image) {
      setStatus("idle");
      return;
    }

    let isActive = true;
    const preloaded = new window.Image();

    setStatus("loading");

    const handleLoaded = () => {
      if (!isActive) return;
      setStatus("loaded");
    };

    const handleError = () => {
      if (!isActive) return;
      setStatus("error");
    };

    preloaded.onload = handleLoaded;
    preloaded.onerror = handleError;
    preloaded.src = image;

    if (preloaded.complete) {
      if (preloaded.naturalWidth > 0) {
        handleLoaded();
      } else {
        handleError();
      }
    }

    return () => {
      isActive = false;
      preloaded.onload = null;
      preloaded.onerror = null;
    };
  }, [image]);

  const hasImage = Boolean(image);
  const showImage = hasImage && status !== "error";
  const isLoading = status === "loading";
  const isLoaded = status === "loaded";

  return (
    <Card
      className={twMerge(
        "pointer-events-none",
        image ? "-translate-x-[75px] translate-y-[50px]" : "translate-y-[50px]"
      )}
    >
      {showImage ? (
        <div className="relative min-w-[300px] h-[168.75px]">
          {isLoading && (
            <Flex className="absolute inset-0" justify="center" align="center">
              <Spinner />
            </Flex>
          )}
          <img
            className={twMerge(
              "w-full h-full transition-opacity duration-300",
              isLoaded ? "opacity-100" : "opacity-0"
            )}
            width={300}
            height={168.75}
            src={image ?? undefined}
            alt=""
          />
        </div>
      ) : (
        <Flex
          className="min-w-[150px] h-[100px]"
          justify="center"
          align="center"
        >
          <RadixText size="2">No preview is available</RadixText>
        </Flex>
      )}
    </Card>
  );
}
